import { Injectable, inject } from '@angular/core';
import Mustache from 'mustache';
import { AssetInstance } from '../models/asset-instance.model';

// Metadata values are plain trusted strings (names/dates), and the resolved
// content is passed through the markdown pipe afterward, which sanitizes the
// final HTML. Mustache's default HTML-escaping would double-escape entities
// (e.g. "&" -> "&amp;" -> "&amp;amp;") once markdown runs its own escaping.
Mustache.escape = (text: string) => text;
import { CheckPoint } from '../models/checkPoint.model';
import { LoggerService } from './logger.service';
import { OrganizationService } from './organization.service';
import { UserService } from './user.service';

export interface TemplateMetadataContext {
  checkpoint: { title: string; instance: string };
  user: { name: string };
  team: { name: string };
  now: { date: string; time: string };
}

/**
 * Placeholders available to form authors via {{checkpoint.title}}-style tags.
 * Drives both the mustache context and the metadata help dialog listing.
 */
export const TEMPLATE_METADATA_FIELDS: { key: string; description: string }[] = [
  { key: 'checkpoint.title', description: 'formBuilder.textLabel.metadataHelp.checkpointTitle' },
  { key: 'checkpoint.instance', description: 'formBuilder.textLabel.metadataHelp.checkpointInstance' },
  { key: 'user.name', description: 'formBuilder.textLabel.metadataHelp.userName' },
  { key: 'team.name', description: 'formBuilder.textLabel.metadataHelp.teamName' },
  { key: 'now.date', description: 'formBuilder.textLabel.metadataHelp.nowDate' },
  { key: 'now.time', description: 'formBuilder.textLabel.metadataHelp.nowTime' },
];

@Injectable({ providedIn: 'root' })
export class TemplateMetadataService {
  private userService = inject(UserService);
  private organizationService = inject(OrganizationService);
  private logger = inject(LoggerService).getLogger('template-metadata');

  /** Builds the mustache context for the checkpoint/instance currently being displayed. */
  buildContext(
    checkPoint: CheckPoint | null | undefined,
    instance: AssetInstance | null | undefined,
  ): TemplateMetadataContext {
    const now = new Date();
    return {
      checkpoint: {
        title: checkPoint?.name ?? '',
        instance: instance ? `#${instance.serial_number}` : '',
      },
      user: {
        name: this.userService.currentUser()?.full_name ?? '',
      },
      team: {
        name: this.organizationService.currentOrganization()?.name ?? '',
      },
      now: {
        date: now.toLocaleDateString(),
        time: now.toLocaleTimeString(),
      },
    };
  }

  /** Resolves {{checkpoint.title}}-style placeholders in `content` against `context`. */
  render(content: string, context: TemplateMetadataContext | null | undefined): string {
    if (!content || !context) {
      return content;
    }
    try {
      return Mustache.render(content, context);
    } catch (error) {
      this.logger.warn('Failed to render template metadata, showing raw content:', error);
      return content;
    }
  }
}
